/**
 * Context Binding Engine — the resolver.
 *
 * Pure pipeline: MATCH → COERCE → GUARD → EMIT(claims) → CbeResolution[].
 *
 *   MATCH  — for each fact, find the binding rows keyed on its tag, then
 *            pick the single best row via the span-aware factMatchers
 *            (`pickBestMatcher`). Disambiguates one-tag-many-fields.
 *   COERCE — run the row's coercion (text + range aware). Returns a typed
 *            CbeValue or `undefined` (unclean ⇒ no fill).
 *   GUARD  — apply the no-fabrication guards (see below). A guard that
 *            trips downgrades the resolution to a skip with a reason.
 *   EMIT   — produce a CbeResolution per matched row (filled OR skipped),
 *            so the UI can surface "no clean value — enter manually".
 *
 * NO-FABRICATION GUARDS (CLAUDE.md §1, §2, §5):
 *   1. clean-coercion-only — `undefined` coercion writes nothing.
 *   2. value-must-appear-in-span — a coerced NUMBER must appear as a token
 *      in its source span (`numericAppearsInSpan`); kills geometry-invented
 *      floats + cross-typed values.
 *   3. midpoint forbidden — a range never collapses to a scalar; a range
 *      value into a scalar-only target leaves the scalar EMPTY (range-hint
 *      skip), it does not fabricate a point.
 *   4. qualifiers don't fabricate precision — already enforced in the
 *      coercion ("~1.5 MW" stays scalar + qualifier).
 *   5. enum only when explicit — `toEnum` returns `undefined` unless a
 *      member is explicitly in the span.
 *   6. negation polarity — `toBool` reads "no/not/not yet/never".
 *
 * Every ratio / division is NaN / Infinity guarded.
 *
 * WIRING STATUS: all four callers are LIVE.
 *   1. Financial pull  — contextFinancialFill.ts / pullFinancialFromSources store action.
 *   2. SED-send        — buildSiteContextBlock (reportPrompts.ts), CBE-A2 markers.
 *   3. Bi-dir sync     — syncSedContextBindings.ts / sedAmendments store path.
 *   4. Intent fill     — contextIntentFill.ts / the store claim-merge paths (gap-fill only).
 */
import {
  toBool,
  toComplexString,
  toDate,
  toEnum,
  toNumber,
  toPercent,
  toRange,
  toYears,
} from './coercions';
import { numericAppearsInSpan } from '../../lib/sed/fieldSchema';
import type { ProjectIntent } from '../../types/siteRag';
import { pickBestMatcher } from './factMatchers';
import { getBindingsForTag } from './contextBindingRegistry';
import {
  CBE_LOW_CONFIDENCE_FLOOR,
  type CbeBinding,
  type CbeFact,
  type CbeProvenance,
  type CbeResolution,
  type CbeSkipReason,
  type CbeValue,
} from './cbeTypes';

/* ── Authoritative enum unions (src/types/siteRag.ts) ─────────────────────
 * The `satisfies` clauses below pin every member array + synonym VALUE to
 * the real `ProjectIntent` union, so the next union change fails `tsc`
 * here instead of silently emitting a stale member at runtime. */
type OfftakeStructure = NonNullable<ProjectIntent['offtake']>['structure'];
type WaterSource = NonNullable<ProjectIntent['waterSource']>['source'];
type WaterRightsStatus = NonNullable<NonNullable<ProjectIntent['waterSource']>['rightsStatus']>;
type CoolingStrategy = ProjectIntent['coolingStrategy'];
type QueueStatus = NonNullable<ProjectIntent['interconnectionStatus']>['queueStatus'];

/**
 * Enum member tables, keyed by target path. The CBE only emits an enum
 * member when it is EXPLICITLY present in the span (never fabricated from
 * absence). Synonym maps widen the keyword surface conservatively.
 */
const ENUM_MEMBERS: Readonly<Record<string, { members: readonly string[]; synonyms?: Readonly<Record<string, string>> }>> = {
  'offtake.structure': {
    members: ['merchant', 'contracted', 'hybrid'] satisfies readonly OfftakeStructure[],
    synonyms: { ppa: 'contracted', 'offtake agreement': 'contracted', spot: 'merchant' } satisfies Readonly<Record<string, OfftakeStructure>>,
  },
  'waterSource.source': {
    members: ['municipal', 'groundwater', 'surface', 'reuse'] satisfies readonly WaterSource[],
    synonyms: { well: 'groundwater', aquifer: 'groundwater', river: 'surface', lake: 'surface', recycled: 'reuse', greywater: 'reuse', city: 'municipal', utility: 'municipal' } satisfies Readonly<Record<string, WaterSource>>,
  },
  'waterSource.rightsStatus': {
    members: ['none', 'applied', 'permitted'] satisfies readonly WaterRightsStatus[],
    synonyms: { granted: 'permitted', approved: 'permitted', pending: 'applied', filed: 'applied' } satisfies Readonly<Record<string, WaterRightsStatus>>,
  },
  // IT/RACK-side cooling axis ONLY. 'undecided' is intentionally NOT a
  // member (indecision is never fabricated from text). Heat-REJECTION-axis
  // terms ('dry cooling', 'evaporative', cooling towers) are intentionally
  // unmapped — they describe `ProjectIntent.bopHeatRejection`, pending a
  // bopHeatRejection binding row.
  'coolingStrategy': {
    members: ['air', 'liquid', 'hybrid'] satisfies readonly CoolingStrategy[],
    synonyms: {
      'direct-to-chip': 'liquid',
      'cold plate': 'liquid',
      immersion: 'liquid',
      'closed loop': 'liquid',
    } satisfies Readonly<Record<string, CoolingStrategy>>,
  },
};

/** A study/agreement named as a prerequisite is not a recorded queue status.
 * Require an affirmative status or completed/current activity in its own
 * clause, rather than allowing either a bare keyword or extracted enum value
 * to override a negated, unknown or prospective source statement. Conflicting
 * affirmative statuses remain unresolved; absence never emits `none`. */
function interconnectionQueueStatus(sourceText: string): QueueStatus | undefined {
  const nonStatus = /\b(?:no|not|never|without|unknown|unconfirmed|unverified|undetermined|uncertain|pending|awaiting|required|requires?|requirements?|needed|needs?|must|should|would|could|may|might|will|planned|proposed|recommended|unless|if|either|or)\b|\bto\s+be\b/i;
  const statusLabel = /\b(?:interconnection|queue|utility[ -]service|load[ -]service)(?:\s+queue)?\s+status\s*(?::|is|=)\s*(pre-application|queued|study|executed-LGIA|executed-load-service)\b/i;
  const affirmed: Array<[QueueStatus, RegExp]> = [
    ['pre-application', /\b(?:project|interconnection|application)\s+(?:is|remains)\s+(?:currently\s+)?(?:in|at)\s+(?:the\s+)?pre-application\b/i],
    ['queued', /\b(?:project|interconnection|application|request)\s+(?:is|remains|has\s+been)\s+(?:currently\s+)?(?:queued|in\s+(?:the\s+)?(?:interconnection\s+)?queue)\b/i],
    ['study', /\b(?:project|interconnection|application)\s+(?:is|remains)\s+(?:currently\s+)?in\s+(?:the\s+)?(?:(?:feasibility|system\s+impact|facilities)\s+)?study\b|\b(?:interconnection|feasibility|system\s+impact|facilities)\s+study\s+(?:(?:is|was|has\s+been)\s+)?(?:underway|in\s+progress|complete|completed)\b/i],
    ['executed-LGIA', /\b(?:executed|signed)\s+(?:an?\s+|the\s+)?(?:LGIA|interconnection\s+agreement)\b|\b(?:LGIA|interconnection\s+agreement)\s+(?:(?:is|was|has\s+been)\s+)?(?:executed|signed|in\s+effect)\b/i],
  ];
  // Explicit load-service agreement acceptance is separate from generator LGIA.
  // Source-role review still binds the agreement to THIS project. A pending,
  // required or negated agreement cannot advance its milestone.
  affirmed.push(['executed-load-service', /\b(?:executed|signed|issued|effective|accepted)\s+(?:(?:an?|the|customer|electric)\s+)*(?:load[ -]service|electric\s+load[ -]service)\s+agreement\b|\b(?:load[ -]service|electric\s+load[ -]service)\s+agreement\s+(?:(?:is|was|has\s+been)\s+)?(?:executed|signed|issued|effective|accepted|in\s+effect)\b/i]);
  const statuses = new Set<QueueStatus>();
  for (const clause of sourceText.split(/[.;\n]|\b(?:but|while)\b/i)) {
    if (nonStatus.test(clause)) continue;
    const labelled = statusLabel.exec(clause)?.[1]?.toLowerCase();
    if (labelled) statuses.add(labelled === 'executed-lgia' ? 'executed-LGIA' : labelled as QueueStatus);
    for (const [status, pattern] of affirmed) if (pattern.test(clause)) statuses.add(status);
  }
  return statuses.size === 1 ? [...statuses][0] : undefined;
}

/**
 * Run the binding's coercion against the fact. Pure dispatch — each
 * coercion is itself pure + returns `undefined` on an unclean parse.
 *
 * `trustValue` (the bi-dir sync caller): an already-typed boolean
 * `claimedValue` is authoritative — `toBool`'s span-negation read must
 * not invert it ("ESA found no wetlands" + claimedValue true would
 * otherwise draft a false amendment). Extraction callers keep the
 * span-authoritative behaviour.
 */
function coerce(binding: CbeBinding, fact: CbeFact, provenance: CbeProvenance, trustValue = false): CbeValue | undefined {
  const { value, sourceText, confidence } = fact;
  if (trustValue && binding.coercion === 'toBool' && typeof value === 'boolean') {
    return { kind: 'bool', value, confidence, provenance };
  }
  switch (binding.coercion) {
    case 'toNumber':
    case 'unitNormalize':
      return toNumber(value, sourceText, confidence, provenance);
    case 'toRange':
      return toRange(value, sourceText, confidence, provenance);
    case 'toDate':
      return toDate(value, sourceText, confidence, provenance);
    case 'toBool':
      return toBool(value, sourceText, confidence, provenance);
    case 'toPercent':
      return toPercent(value, sourceText, confidence, provenance);
    case 'toYears':
      return toYears(value, sourceText, confidence, provenance);
    case 'toString':
      return toComplexString(value, sourceText, confidence, provenance);
    case 'toEnum': {
      if (binding.targetField.path === 'interconnectionStatus.queueStatus') {
        const status = interconnectionQueueStatus(sourceText);
        return status === undefined ? undefined : { kind: 'enum', value: status, confidence, provenance };
      }
      const table = ENUM_MEMBERS[binding.targetField.path];
      if (!table) return undefined; // no member table ⇒ cannot emit an enum cleanly
      return toEnum(value, sourceText, table.members, confidence, provenance, table.synonyms);
    }
    default: {
      const _exhaustive: never = binding.coercion;
      return _exhaustive;
    }
  }
}

/** Is this target path a scalar-only field (no range widget)? Conservative
 *  default: everything is scalar-only UNLESS the binding declared
 *  `valueKind: 'range'`, in which case the target is range-capable. */
function targetIsRangeCapable(binding: CbeBinding): boolean {
  return binding.valueKind === 'range';
}

/**
 * Apply the no-fabrication GUARDS to a coerced value. Returns either the
 * (possibly unchanged) value to EMIT, or a skip reason. Pure.
 *
 * `trustValue` (CBE-A3, the bi-dir sync caller) skips GUARD 2 ONLY: the
 * facts there are already-typed, trusted claim values, and a user-EDITED
 * `claimedValue` legitimately no longer appears in the original
 * `sourceSpan`. The coercion-cleanliness + midpoint guards still apply.
 */
function guard(
  binding: CbeBinding,
  fact: CbeFact,
  value: CbeValue,
  trustValue: boolean,
): { value: CbeValue } | { skip: CbeSkipReason } {
  // GUARD 2 — value-must-appear-in-span (numeric only).
  // A coerced number (scalar / multi total / range endpoint) must appear as
  // a token in the span, else it was invented (geometry-derived float, a
  // cross-typed figure). String / date / enum / bool values are span-derived
  // by construction and are exempt from the numeric token check.
  const span = fact.sourceText ?? '';
  if (!trustValue && value.kind === 'scalar' && typeof value.value === 'number') {
    if (span && !numericAppearsInSpan(value.value, span)) {
      return { skip: 'value-not-in-span' };
    }
  }
  if (!trustValue && value.kind === 'multi') {
    // The per-unit + count are what the prose states ("4 x 150"); the total
    // is COMPUTED (count×perUnit) and so need not appear verbatim.
    if (span && (!numericAppearsInSpan(value.count, span) || !numericAppearsInSpan(value.perUnit, span))) {
      return { skip: 'value-not-in-span' };
    }
  }
  if (!trustValue && value.kind === 'range') {
    // Each FINITE endpoint must appear in the span. An open endpoint (null)
    // is exempt. Scale-suffixed endpoints (e.g. ×1e9) are checked against
    // the bare stated figure too, so we accept either the raw or scaled form.
    const endpoints = [value.min, value.max].filter((n): n is number => typeof n === 'number');
    for (const n of endpoints) {
      if (!span) break;
      if (numericAppearsInSpan(n, span)) continue;
      // Accept the de-scaled figure too (e.g. 3.0 for 3.0e9 from "$3.0B").
      const descaled = descaleCandidates(n);
      if (descaled.some(d => numericAppearsInSpan(d, span))) continue;
      return { skip: 'value-not-in-span' };
    }
  }

  // GUARD 3 — MIDPOINT FORBIDDEN. A range into a scalar-only target leaves
  // the scalar empty (range-hint skip), never collapses to a point.
  if (value.kind === 'range' && !targetIsRangeCapable(binding)) {
    return { skip: 'range-no-scalar' };
  }

  return { value };
}

/**
 * De-scaled candidate magnitudes for a currency-scaled endpoint, so the
 * span check accepts the bare stated figure ("3.0" for 3.0e9 from "$3.0B").
 * NaN / Infinity guarded.
 */
function descaleCandidates(n: number): number[] {
  if (!Number.isFinite(n) || n === 0) return [];
  const out: number[] = [];
  for (const scale of [1_000, 1_000_000, 1_000_000_000]) {
    const d = n / scale;
    if (Number.isFinite(d)) out.push(d);
  }
  return out;
}

/** Options for {@link resolveContextBindings}. */
export interface ResolveContextBindingsOptions {
  /**
   * Skip GUARD 2 (value-must-appear-in-span). For callers whose facts carry
   * already-typed TRUSTED values (the bi-dir sync path coerces existing —
   * possibly user-edited — `claimedValue`s, which need not appear in the
   * original span). Coercion-cleanliness + midpoint guards still apply.
   */
  readonly trustValue?: boolean;
}

/**
 * Resolve a batch of facts into CbeResolutions. Pure.
 *
 * A single tag may fan out to MANY distinct target fields (`water.source`
 * → both a SED stress-level field AND an intent enum; `schedule.inservice`
 * → both an intent COD and a per-phase COD). So we DO NOT collapse to one
 * row per fact: we group a fact's candidate bindings by their distinct
 * TARGET FIELD (`surface:path`) and, WITHIN each target group, pick the
 * single best matcher (the which-of-many disambiguation — 332-vs-800,
 * NTP-vs-COD). This yields one resolution per distinct target that a row
 * fires for. Facts whose tag has no binding, or whose every target group
 * matches no row, produce NO resolution (silently unmapped — by design).
 *
 * @param facts the {tag,value,sourceText,confidence} projection of the
 *              site's `ExtractedClaim[]`.
 * @param options.trustValue skip GUARD 2 (value-must-appear-in-span) for
 *              already-typed trusted values (e.g. user-edited claims in the
 *              bi-dir sync path). Default false — behaviour unchanged.
 */
export function resolveContextBindings(
  facts: readonly CbeFact[],
  options?: ResolveContextBindingsOptions,
): CbeResolution[] {
  const trustValue = options?.trustValue === true;
  const out: CbeResolution[] = [];

  for (const fact of facts) {
    const candidates = getBindingsForTag(fact.tag);
    if (candidates.length === 0) continue;

    const provenance: CbeProvenance = { tag: fact.tag, sourceSpan: fact.sourceText };
    const lowConfidence = Number.isFinite(fact.confidence) && fact.confidence < CBE_LOW_CONFIDENCE_FLOOR;

    // Group candidate rows by distinct target field (preserve declared
    // order within each group so `pickBestMatcher`'s stable fallback holds).
    const byTarget = new Map<string, CbeBinding[]>();
    for (const c of candidates) {
      const key = `${c.targetField.surface}:${c.targetField.path}`;
      const list = byTarget.get(key) ?? [];
      list.push(c);
      byTarget.set(key, list);
    }

    for (const group of byTarget.values()) {
      const binding = pickBestMatcher(group, fact);
      if (binding === null) continue; // no row in this target group fired

      // COERCE.
      const coerced = coerce(binding, fact, provenance, trustValue);

      // GUARD 1 — clean-coercion-only.
      if (coerced === undefined) {
        // GUARD 5 — a failed toEnum means no member was EXPLICIT in the
        // span (toEnum reads the span, not just the value, so 'no-value' /
        // 'unclean-coercion' would mislabel the reason).
        const skipReason: CbeSkipReason =
          binding.coercion === 'toEnum'
            ? 'enum-not-explicit'
            : fact.value === undefined || fact.value === ''
              ? 'no-value'
              : 'unclean-coercion';
        out.push({
          binding,
          targetField: binding.targetField,
          value: undefined,
          skipReason,
          lowConfidence,
        });
        continue;
      }

      // GUARD 2-3.
      const guarded = guard(binding, fact, coerced, trustValue);
      if ('skip' in guarded) {
        out.push({
          binding,
          targetField: binding.targetField,
          value: undefined,
          skipReason: guarded.skip,
          lowConfidence,
        });
        continue;
      }

      // EMIT.
      out.push({
        binding,
        targetField: binding.targetField,
        value: guarded.value,
        lowConfidence,
      });
    }
  }

  return out;
}

/**
 * Convenience: only the resolutions that produced a value (filled). Pure.
 */
export function filledResolutions(resolutions: readonly CbeResolution[]): CbeResolution[] {
  return resolutions.filter(r => r.value !== undefined);
}
